import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";

import { buildMiniMapDataArgs, useModelRevisions } from "apis/procore/modelRevisions";
import { useGetMiniMapDataQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import {
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
    selectSceneId,
} from "slices/explorer";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { downloadDrawingData } from "./downloadDrawingData";
import { selectCurrentDrawing, setMiniMapWidgetStatus } from "./minimapSlice";
import { buildTilingRequest } from "./tiledDrawing";

const l = log.miniMap.utils;

/**
 * Subscribe to the mini map data query for the active scene.
 *
 * Implementation notes:
 *
 *   1. Two-query chain: the shared `useModelRevisions` query resolves the
 *      opened scene's revision, and with it the BIM model, then
 *      `useGetMiniMapDataQuery` fetches plans → locations → drawings for that
 *      model. Both use `skipToken` until their inputs are ready. The sequential
 *      dependency (revision before plans) is inherent — plans are requested by
 *      model id, which the host doesn't pass — so the two-query structure
 *      reflects reality rather than adding a waterfall.
 *
 *   2. The slice's `extraReducers` matcher mirrors the `getMiniMapData` cache
 *      into slice state, so existing selectors keep working without changes.
 *
 *   3. The per-drawing image for the *current* drawing is downloaded here
 *      because `handleCameraUpdated` (in `miniMapWeb` / `miniMapMobile`)
 *      only kicks off a fetch when the *target* drawing differs from the
 *      current one, so it never fetches the drawing that is already current
 *      on mount / re-enable. The download is keyed on the *slice's* current
 *      drawing (`selectCurrentDrawing`) — NOT `data.currentDrawingId` from the
 *      query payload. Those two diverge: the camera handler repoints the slice
 *      current drawing via `setCurrentDrawing` when the user changes level,
 *      including while the mini map is hidden (where it sets the current
 *      drawing but skips the download because `enabled` is false). On
 *      re-enable the camera handler then sees `target === current` and skips
 *      the download, so this effect must download whatever drawing is actually
 *      current — otherwise the displayed drawing stays on the loading shimmer
 *      forever (BM-11644).
 *
 *      The download is driven off the current drawing's *status*: it only kicks
 *      off from `Initial`, transitions it to `Loading`, then `Success`/`Error`.
 *      Depending on the status (rather than a ref) is what makes
 *      `forceMiniMapRefresh` work: the matcher resets every drawing's
 *      `asyncState` back to `Initial` on each fulfilled fetch, and when the
 *      current drawing keeps the same id across the refresh only the status
 *      changes (Success → Initial). Keying on status re-runs the effect and
 *      re-downloads; keying only on id/url would leave it stranded on the
 *      shimmer forever.
 *
 * Lives in its own file (rather than `miniMapUtils.tsx`) so that pure-utility
 * consumers — and their tests — do not transitively pull in `procoreApi`,
 * which would create a circular import via `procoreClient → app/store`.
 *
 * @param enabled When `false`, the per-drawing image download is suppressed.
 *   Callers pass the mini map visibility config here so that a hidden mini map
 *   does not download its drawing. The lightweight metadata queries keep
 *   running so the slice stays populated; this — together with the
 *   already-downloaded guard below — preserves the BM-7305 behavior where a
 *   drawing image survives a visibility toggle without re-fetching.
 *
 * @returns `{ isLoading }` — `true` while the initial metadata round-trip
 *   (revisions → mini map data) has not yet settled. Callers should render a
 *   loading skeleton instead of the empty state during this window so the UI
 *   does not flash empty → sheets once the API responds.
 */
export const useMiniMapData = (enabled = true) => {
    const procoreContext = useAppSelector(selectProcoreContext);
    const dispatch = useAppDispatch();
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const tilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const sceneId = useAppSelector(selectSceneId);

    // Shared, session-cached revisions query (reused across the app).
    const {
        data: revisions,
        sceneId: revisionsSceneId,
        isLoading: isRevisionsLoading,
        isFetching: isRevisionsFetching,
        isSuccess: isRevisionsSuccess,
        isError: isRevisionsError,
    } = useModelRevisions();

    const hasProcoreContext = Boolean(
        procoreContext?.companyId && procoreContext.projectId && procoreContext.sceneVersionId,
    );
    const miniMapDataArgs = hasProcoreContext
        ? buildMiniMapDataArgs(procoreContext!, revisions, revisionsSceneId)
        : undefined;
    const queryArgs = miniMapDataArgs ?? skipToken;

    // Keep the query subscribed so the slice stays mirrored via the matcher.
    // The download below is driven off the slice's current drawing, not this
    // query's payload (see implementation note 3).
    const {
        isLoading: isMiniMapLoading,
        isSuccess: isMiniMapSuccess,
        isError: isMiniMapError,
    } = useGetMiniMapDataQuery(queryArgs);

    // Treat the metadata pipeline as loading until it has a terminal result.
    // Covers both the revisions hop and the mini-map-data hop so the empty
    // state is only shown after we know there are no navigable sheets.
    const isWaitingForRevisions =
        hasProcoreContext && !miniMapDataArgs && (isRevisionsLoading || isRevisionsFetching || !isRevisionsSuccess);
    const isWaitingForMiniMapData = queryArgs !== skipToken && !isMiniMapSuccess && !isMiniMapError;
    const isLoading =
        hasProcoreContext &&
        !isRevisionsError &&
        (isWaitingForRevisions || isWaitingForMiniMapData || isMiniMapLoading);
    // Surface any terminal fetch failure so callers can render a distinct error
    // state rather than falling through to the "no sheets" empty card.
    const isError = hasProcoreContext && (isRevisionsError || isMiniMapError);

    // Mirror the predicates above into the slice so `selectIsMiniMapWidgetShown`
    // (Settings toggle, `useViewportSafeArea`, and `MiniMapWeb` itself) is driven
    // by exactly the same values the widget renders from. A matcher can't do this:
    // the revisions query is shared app-wide so its pending state doesn't imply
    // the mini map is waiting on it, and `getMiniMapData.matchPending` only flips
    // after the revisions hop has already put the shimmer on screen.
    //
    // The cleanup keeps the flags from sticking if the last consumer of this hook
    // unmounts mid-flight, which would otherwise reserve the corner forever.
    useEffect(() => {
        dispatch(setMiniMapWidgetStatus({ loading: isLoading, error: isError }));
        return () => {
            dispatch(setMiniMapWidgetStatus({ loading: false, error: false }));
        };
    }, [dispatch, isLoading, isError]);

    // Download the drawing that is actually current in the slice. Keyed on the
    // identity (id + url) and the *preview* status so the effect re-runs both
    // when the current drawing changes and when its status is reset back to
    // `Initial` (e.g. by `forceMiniMapRefresh`, which re-mirrors the query
    // payload and resets every drawing's async states). The preview status is
    // authoritative here because the mini map only consumes the preview and
    // `downloadDrawingData` moves both states out of `Initial` together.
    const currentDrawingId = currentDrawing?.id;
    const currentDrawingUrl = currentDrawing?.drawingData.url;
    const currentDrawingStatus = currentDrawing?.drawingData.previewAsyncState.status;

    useEffect(() => {
        // Don't download the drawing image when the mini map is turned off.
        if (!enabled) {
            return;
        }
        if (!currentDrawing || !currentDrawingId || !currentDrawingUrl) {
            return;
        }

        // Only kick off a download from `Initial`. Once we set `Loading` (and
        // later `Success`/`Error`) this effect re-runs but no-ops here, so it
        // never restarts an in-flight or completed download — while a reset back
        // to `Initial` (forceMiniMapRefresh) does re-trigger it.
        if (currentDrawingStatus !== AsyncStatus.Initial) {
            return;
        }

        downloadDrawingData({
            dispatch,
            drawingId: currentDrawingId,
            url: currentDrawingUrl,
            telemetrySource: "miniMap.useMiniMapData.fetchDrawingData",
            tiling: buildTilingRequest(
                tilingEnabled,
                procoreContext,
                sceneId,
                currentDrawing.drawingData,
                isMobileDesign,
            ),
        }).then(() => {
            l.debug("Finished downloading drawing data for mini map:", currentDrawingId);
        });
    }, [
        currentDrawing,
        currentDrawingId,
        currentDrawingUrl,
        currentDrawingStatus,
        dispatch,
        enabled,
        tilingEnabled,
        isMobileDesign,
        procoreContext,
        sceneId,
    ]);

    return { isLoading, isError };
};
